import { useState } from 'react';
import { api } from '../lib/api';

export interface FieldDef<T> {
  key: keyof T & string;
  label: string;
  type?: 'text' | 'color' | 'select';
  options?: string[];
  width?: number | string;
  render?: (value: unknown, item: T) => React.ReactNode;
}

interface SettingsTableProps<T extends { id: string }> {
  resource: string;
  title: string;
  items: T[];
  loading: boolean;
  fields: FieldDef<T>[];
  defaults: Partial<T>;
  isAdminOnly?: boolean;
  canEdit?: boolean;
}

export function SettingsTable<T extends { id: string }>({
  resource,
  title,
  items,
  loading,
  fields,
  defaults,
  canEdit = true,
}: SettingsTableProps<T>) {
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  function startEdit(item: T) {
    setEditing(item.id);
    setDraft(item as unknown as Record<string, unknown>);
    setCreating(false);
  }

  function startCreate() {
    setCreating(true);
    setEditing(null);
    setDraft({ ...(defaults as Record<string, unknown>) });
  }

  async function save() {
    setSaving(true);
    try {
      if (creating) {
        await api.create(resource, draft);
      } else if (editing) {
        await api.patch(resource, editing, draft);
      }
      setEditing(null);
      setCreating(false);
      setDraft({});
    } catch (err) {
      alert(`Save failed: ${err instanceof Error ? err.message : err}`);
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    if (!confirm('Delete this item?')) return;
    try {
      await api.delete(resource, id);
    } catch (err) {
      alert(`Delete failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  function renderField(field: FieldDef<T>, value: unknown, onChange: (v: unknown) => void) {
    if (field.type === 'color') {
      return (
        <input
          type="color"
          value={(value as string) || '#3B82F6'}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: 40, height: 28, border: '1px solid #d1d5db', borderRadius: 4 }}
        />
      );
    }
    if (field.type === 'select' && field.options) {
      return (
        <select
          value={(value as string) || field.options[0]}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        >
          {field.options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    }
    return (
      <input
        type="text"
        value={(value as string) || ''}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
      />
    );
  }

  return (
    <section>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>{title}</h2>
        {canEdit && !creating && !editing && (
          <button onClick={startCreate} style={btnPrimary}>
            + New
          </button>
        )}
      </div>
      {loading && <div style={{ color: '#6b7280' }}>Loading…</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left', color: '#6b7280' }}>
            {fields.map((f) => (
              <th key={f.key} style={{ padding: '8px 4px', width: f.width }}>
                {f.label}
              </th>
            ))}
            {canEdit && <th style={{ width: 120 }}></th>}
          </tr>
        </thead>
        <tbody>
          {creating && (
            <tr style={{ background: '#fffbeb' }}>
              {fields.map((f) => (
                <td key={f.key} style={{ padding: 4 }}>
                  {renderField(f, draft[f.key], (v) => setDraft({ ...draft, [f.key]: v }))}
                </td>
              ))}
              <td>
                <button onClick={save} disabled={saving} style={btnPrimary}>
                  {saving ? 'Saving…' : 'Create'}
                </button>
                <button onClick={() => setCreating(false)} style={btnSecondary}>
                  Cancel
                </button>
              </td>
            </tr>
          )}
          {items.map((item) => {
            const isEditing = editing === item.id;
            return (
              <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                {fields.map((f) => (
                  <td key={f.key} style={{ padding: '6px 4px' }}>
                    {isEditing
                      ? renderField(f, draft[f.key], (v) => setDraft({ ...draft, [f.key]: v }))
                      : f.render
                        ? f.render((item as Record<string, unknown>)[f.key], item)
                        : String((item as Record<string, unknown>)[f.key] ?? '')}
                  </td>
                ))}
                {canEdit && (
                  <td style={{ padding: '6px 4px' }}>
                    {isEditing ? (
                      <>
                        <button onClick={save} disabled={saving} style={btnPrimary}>
                          {saving ? '…' : 'Save'}
                        </button>
                        <button onClick={() => setEditing(null)} style={btnSecondary}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => startEdit(item)} style={btnSecondary}>
                          Edit
                        </button>
                        <button onClick={() => remove(item.id)} style={btnDanger}>
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

const inputStyle: React.CSSProperties = {
  border: '1px solid #d1d5db',
  borderRadius: 4,
  padding: '4px 8px',
  fontSize: 13,
  width: '100%',
};
const btnPrimary: React.CSSProperties = {
  padding: '4px 10px',
  background: '#2563eb',
  color: 'white',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 12,
  marginRight: 4,
};
const btnSecondary: React.CSSProperties = {
  padding: '4px 10px',
  background: 'white',
  color: '#374151',
  border: '1px solid #d1d5db',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 12,
  marginRight: 4,
};
const btnDanger: React.CSSProperties = {
  padding: '4px 10px',
  background: 'white',
  color: '#dc2626',
  border: '1px solid #fca5a5',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: 12,
};
